import React, { FC, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Background from '../../components/Background/Background';
import Header from '../../components/Header/Header';
import { planeVariants } from '../../animations/plane';
import fadeIn from '../../animations/fadeIn';
import './_styles.signin.css';
import Logo from '../../components/Logo/Logo';
import { TextField } from '@mui/material';
import Button from '../../components/Button/Button';
import { Link, useNavigate } from 'react-router-dom';
import AppleLogin from 'react-apple-login';
import { useGoogleLogin } from '@react-oauth/google';
import { useAppDispatch, useAppSelector } from '../../hooks/useApp';
import {
  login,
  loginWithGoogle,
  register,
  resetStatus,
  loginWithApple
} from '../../store/slices/authSlice';
import Modal from '../../components/Modal/Modal';
import { baseURL, redirectENV } from '../../api';

interface AuthUIProps {
  type: 'signin' | 'signup';
}

const AuthUI: FC<AuthUIProps> = ({ type }) => {
  const navigate = useNavigate();

  const [forgotPassword, setForgotPassword] = useState(false);

  const [isSocialLogin, setIsSocialLogin] = useState(false);

  const dispatch = useAppDispatch();
  const { status, error } = useAppSelector((state) => state.auth);
  const [userDetails, setUserDetails] = useState({
    email: '',
    password: ''
  });

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      dispatch(loginWithGoogle(tokenResponse.access_token));
    }
  });

  const {clientId, source} = useAppSelector(state => state.auth)

  const appleResponse = (response: any) => {
    if (response.error) {
      return;
    }
    dispatch(loginWithApple(response));
  };

  useEffect(() => {
    if (status === 'success') {
      dispatch(resetStatus());
      if (type === 'signup' && !isSocialLogin) {
        navigate('/confirmemail?email=' + userDetails.email);
      } else {
        navigate('/points', { replace: true });
      }
    }

    return () => {};
  }, [dispatch, isSocialLogin, navigate, status, type, userDetails.email]);

  const registerNewUser = () => {
    analytics.track('Register Button Clicked', {location: window.location.href, clientId, source});
    setIsSocialLogin(false);
    dispatch(register(userDetails));
  };

  const loginUser = () => {
    analytics.track('Login Button Clicked', {location: window.location.href, clientId, source});
    dispatch(login(userDetails));
  };

  return (
    <>
      <Modal
        visible={status === 'failed'}
        onClose={() => dispatch(resetStatus())}
      >
        <p className='text-error font-bold text-xl'>Error.</p>
        {error?.split('\n').map((line, index) => (
          <p>{line}</p>
        ))}
      </Modal>
      <Modal visible={forgotPassword} onClose={() => setForgotPassword(false)}>
        <p className='text-primary font-bold'>Oh no!</p>
        <p className='text-xs'>
          We do not have access to your email address, please enter your email
          address to proceed.
        </p>
        <TextField
          id='standard-basic'
          label='Email Address'
          variant='standard'
          className='w-full'
          onChange={(e) =>
            setUserDetails({ ...userDetails, email: e.target.value })
          }
        />
        <Button
          onClick={() => navigate(`/resetcode?email=${userDetails.email}`)}
          delay={-0.5}
        >
          Let's Try Again
        </Button>
      </Modal>
      <Background image='login-bg'>
        <Header title={type === 'signin' ? 'Sign In' : 'Sign Up'} />
        <motion.img
          initial='hidden'
          animate='enter'
          exit='exit'
          src='/plane.png'
          className='signin-plane'
          variants={planeVariants}
        />
        <div className='auth-container'>
          <motion.div
            variants={fadeIn(0.5)}
            initial='hidden'
            animate='enter'
            exit='exit'
            className='auth-logo'
          >
            <Logo name='awayz' className='max-w-[100%]' />
          </motion.div>
          <div className='w-full flex flex-col gap-6 items-center'>
            <motion.p
              variants={fadeIn(0.45)}
              initial='hidden'
              animate='enter'
              exit='exit'
              className='w-full text-xl font-semibold'
            >
              {type === 'signin' ? 'Sign In' : 'Sign Up'}:
            </motion.p>
            <motion.div
              variants={fadeIn(0.4)}
              initial='hidden'
              animate='enter'
              exit='exit'
              className='auth-logo'
            >
              <img
                onClick={() => {
                  setIsSocialLogin(true);
                  googleLogin();
                }}
                src='/socials/Google-icon.jpg'
                className='aspect-square w-14 rounded-xl cursor-pointer'
                alt='Google Icon'
              />
              <AppleLogin
                clientId='com.typedev.awayz'
                scope='email name'
                redirectURI={`${baseURL}/tp/apple/get/user?origin=${redirectENV}/socialApple`}
                callback={appleResponse}
                responseType='id_token code'
                state=''
                responseMode='form_post'
                render={({ onClick }) => (
                  <img
                    onClick={() => {
                      setIsSocialLogin(true);
                      onClick();
                    }}
                    src='/socials/Apple-icon.jpg'
                    className='aspect-square w-14 rounded-xl cursor-pointer'
                    alt='appleLogin'
                  />
                )}
              />
            </motion.div>
            <motion.p
              variants={fadeIn(0.35)}
              initial='hidden'
              animate='enter'
              exit='exit'
            >
              OR
            </motion.p>
            <motion.p
              variants={fadeIn(0.3)}
              initial='hidden'
              animate='enter'
              exit='exit'
              className='w-full text-lg'
            >
              Enter your details below:
            </motion.p>
            <motion.div
              variants={fadeIn(0.15)}
              initial='hidden'
              animate='enter'
              exit='exit'
              className='w-full'
            >
              <TextField
                id='standard-basic'
                label='Email Address'
                variant='standard'
                className='w-full'
                onChange={(e) =>
                  setUserDetails({
                    ...userDetails,
                    email: e.target.value
                  })
                }
                required={true}
              />
            </motion.div>
            <motion.div
              variants={fadeIn(0.1)}
              initial='hidden'
              animate='enter'
              exit='exit'
              className='w-full'
            >
              <TextField
                id='standard-basic'
                label='Password'
                variant='standard'
                className='w-full text-white'
                type='password'
                onChange={(e) =>
                  setUserDetails({
                    ...userDetails,
                    password: e.target.value
                  })
                }
                required={true}
              />
            </motion.div>
            <Button
              delay={0.05}
              onClick={type === 'signin' ? loginUser : registerNewUser}
              loading={status === 'loading'}
            >
              {type === 'signin' ? 'Sign In' : 'Sign Up'}
            </Button>
            <motion.p
              className='text-sm'
              variants={fadeIn(0.025)}
              initial='hidden'
              animate='enter'
              exit='exit'
            >
              {type === 'signin' ? (
                <>
                  Don't have an account?{' '}
                  <Link to='/signup' replace>
                    Sign up here
                  </Link>
                </>
              ) : (
                <>
                  Have an account?{' '}
                  <Link to='/signin' replace>
                    Sign in here
                  </Link>
                </>
              )}
            </motion.p>
            {type === 'signin' && (
              <motion.button
                className='text-sm text-primary font-bold'
                variants={fadeIn()}
                initial='hidden'
                animate='enter'
                exit='exit'
                onClick={() => setForgotPassword(true)}
              >
                Forgot Password?
              </motion.button>
            )}
          </div>
        </div>
      </Background>
    </>
  );
};

export default AuthUI;
